import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Truck, ArrowRight, ChevronRight, Phone, Calculator as CalculatorIcon, CheckCircle2, ReceiptText, Clock, Ruler, ClipboardList } from 'lucide-react';
import { SERVICE_PRODUCT_SLUGS, getServiceCity, getServiceProduct, getNearbyCities } from '../data/cityMaterialContent';
import { CityMiniMap } from '../components/CityMiniMap';
import { getCityLocal } from '../data/cityLocalContent';
import { allReviews } from '../components/Reviews';
import { projects } from '../data/projects';
import { getCountyForCity } from '../data/counties';
import { categories } from '../data/products';
import { JsonLd, useSEO } from '../utils/seo';
import { localBusinessProvider } from '../utils/localBusiness';
import { NotFound } from './NotFound';

// Legacy curated zone (1–5) → the three tiers customers actually see.
const ZONE_TIER: Record<number, 1 | 2 | 3> = {
  1: 1,
  2: 1,
  3: 2,
  4: 3,
  5: 3
};
const ZONE_FEE: Record<number, number> = {
  1: 80,
  2: 80,
  3: 120,
  4: 175,
  5: 175
};
// A standard tandem load. Used for the worked delivered-price example so the
// page states one comparable number rather than a per-yard rate.
const EXAMPLE_YARDS = 12;

// The materials people actually search for alongside "gravel delivery" —
// crushed rock, drain rock, pea gravel, topsoil. Each row links straight into
// the product page, so this section doubles as the page's internal-linking
// hub and as a plain-language "what do I order for my job" index.
const POPULAR_MATERIALS: {
  slug: string;
  use: string;
}[] = [{
  slug: '5-8-minus',
  use: 'Driveways, pathways, and compacted surfaces'
}, {
  slug: '1-25-minus',
  use: 'Driveway base and soft, muddy ground'
}, {
  slug: '5-8-clear',
  use: 'Drainage and clean landscape rock'
}, {
  slug: '7-8-drain-rock',
  use: 'French drains, footing drains, dry wells'
}, {
  slug: 'pea-gravel',
  use: 'Paths, play areas, and landscape fill'
}, {
  slug: '3-way-topsoil',
  use: 'New lawns, garden beds, and regrading'
}];

/**
 * City landing page — /gravel-delivery/:citySlug.
 *
 * The site already ranked on city × material, but had nothing answering the
 * plain "gravel delivery <city>" query, which is where the volume is. This
 * page aggregates the real catalog, the real zone fee for that city, and the
 * local material vocabulary a national broker doesn't use.
 */
export function City() {
  const {
    locationSlug = ''
  } = useParams<{
    locationSlug: string;
  }>();
  const city = getServiceCity(locationSlug);
  useSEO({
    title: city ? `Gravel Delivery in ${city.name}, WA — Delivered Price Up Front | 206Gravel` : 'Gravel Delivery Service Areas | 206Gravel',
    description: city ? `Bulk gravel, crushed rock, topsoil, and bark delivered in ${city.name}, WA. Our own trucks and drivers, delivered total before you order, pay on delivery.` : 'Bulk gravel and aggregate delivery across the Puget Sound.',
    path: city ? `/gravel-delivery/${city.slug}` : '/service-areas'
  });
  if (!city) return <NotFound data-id="element-2463" data-mp="14vdlxqxpjoj2" />;
  const county = getCountyForCity(city);
  const path = `/gravel-delivery/${city.slug}`;
  const fee = ZONE_FEE[city.zone];
  const tier = ZONE_TIER[city.zone];
  const featured = SERVICE_PRODUCT_SLUGS.map(slug => getServiceProduct(slug)).filter(Boolean) as {
    product: any;
    category: any;
  }[];
  const allProducts = categories.flatMap(c => c.products);
  const lowestPrice = allProducts.length ? Math.min(...allProducts.map(p => p.pricePerYard)) : 0;
  // Worked example on the cheapest featured material — the number a customer
  // can actually put next to a competitor's "free delivery" quote.
  const example = featured.length ? featured.reduce((lo, m) => m.product.pricePerYard < lo.product.pricePerYard ? m : lo, featured[0]) : null;
  const exampleTotal = example ? example.product.pricePerYard * EXAMPLE_YARDS + fee : 0;
  // Nearest six by actual distance, not by county — see getNearbyCities.
  const nearby = getNearbyCities(city, 6);
  // Hand-authored per-city content: delivery access notes, two city-specific
  // FAQs, and the project guides that match the work this city actually orders.
  const local = getCityLocal(city.slug);
  const localProjects = (local?.projectSlugs ?? []).map(slug => projects.find(p => p.slug === slug)).filter(Boolean) as typeof projects;
  // Real completed deliveries in this city, drawn from the customer reviews
  // that came in with a photo. This is the one kind of proof a national broker
  // generating city pages cannot fabricate, so it leads over any copy we could
  // write. Cities with no photographed delivery yet simply omit the block —
  // an empty grid of placeholders would undo the credibility it is here for.
  const deliveryExamples = allReviews.filter(r => r.photo && r.location.includes(city.name)).slice().sort((a, b) => b.date.localeCompare(a.date)).slice(0, 3);
  const popular = POPULAR_MATERIALS.map(m => {
    const product = allProducts.find(p => p.slug === m.slug);
    return product ? {
      product,
      use: m.use
    } : null;
  }).filter(Boolean) as {
    product: any;
    use: string;
  }[];
  // The four curated materials already appear as image cards above, so the
  // secondary link row only carries what isn't shown twice.
  const alsoPopular = popular.filter(p => !featured.some(f => f.product.slug === p.product.slug));
  const sharedFaqs = [{
    q: `How much does gravel delivery cost in ${city.name}?`,
    a: `${city.name} sits in delivery Zone ${tier}, so the haul is $${fee} per truckload on top of the material. A ${EXAMPLE_YARDS}-yard load of ${example ? example.product.name : 'crushed rock'} delivered to ${city.name} comes to about $${exampleTotal.toLocaleString()} all in. We quote the material and the haul as separate lines so you can check both — "free delivery" offers still pay for the truck, they just bury it in the per-yard price.`
  }, {
    q: `How fast can you deliver to ${city.name}?`,
    a: `Same-day on most orders placed before noon, subject to truck availability; later orders typically go out the next business day. We run our own trucks and our own drivers, so scheduling doesn't depend on a third-party hauler picking up the job.`
  }, {
    q: `What's the minimum gravel order for ${city.name}?`,
    a: `Three cubic yards. Loads under three yards carry a flat $250 micro-load delivery fee, so if your project can use the material it's usually worth rounding up toward a full load.`
  }, {
    q: `Do I need to be home for the delivery in ${city.name}?`,
    a: `No. Most customers mark the drop spot with a cone, a stake, or a tarp and leave a note at checkout. Our driver will call on approach if anything looks unclear.`
  }, {
    q: `How do I pay?`,
    a: `On delivery — no card at checkout. Cash, credit or debit card, Apple Pay, or Google Pay when the load arrives in ${city.name}.`
  }, {
    q: `Which ${city.name} neighborhoods do you deliver to?`,
    a: `All of them, including ${city.neighborhoods.join(', ')}. Enter your address in the estimator and it returns your zone and delivered total straight away.`
  }, {
    q: `What is the best gravel for a ${city.name} driveway?`,
    a: `For a driveway surface, 5/8" Minus is the standard here — crushed rock with the fines left in, so it compacts into a hard, stable finish. If the ground underneath is soft or rutted, build the base with 1.25" Minus first (4 inches, compacted), then top it with 2–3 inches of 5/8" Minus. Never use a clean or washed rock like pea gravel as a driving surface; with no fines it never locks together.`
  }, {
    q: `What gravel is best for drainage in ${city.name}?`,
    a: `Use a clean, washed rock with no fines so water moves through it — 7/8" Drain Rock for French drains, footing drains, and dry wells, or 5/8" Crushed Clear where you want a smaller, easier-to-shovel stone. Minus materials are the wrong choice for drainage: the fines that make them compact are exactly what clogs the voids.`
  }, {
    q: `Do you deliver gravel to residential properties in ${city.name}?`,
    a: `Yes. Most of what we haul into ${city.name} goes to homeowners — driveways, garden beds, drainage projects, and patio bases — alongside contractor jobsites. We just need a flat drop spot roughly 10 ft wide by 20 ft long with about 12 ft of overhead clearance for the truck bed.`
  }, {
    q: `How much gravel do I need?`,
    a: `Measure the length and width of the area and the depth you want, then run the numbers through our material calculator — it converts to cubic yards and rounds to a deliverable load. As a rule of thumb, one yard covers about 100 sq ft at 3 inches deep. If you are between sizes, call dispatch and we will work it out with you.`
  }];
  // City-specific questions sit second and third, right behind the cost
  // question everyone opens with. Putting them there means the part of the page
  // that is genuinely about THIS city is the part a reader actually reaches,
  // rather than being buried under ten answers identical to every other page.
  const faqs = [sharedFaqs[0], ...(local?.localFaqs ?? []), ...sharedFaqs.slice(1)];
  return <main className="bg-[#fdfbf7] dark:bg-[#222222] min-h-screen" data-id="element-2464" data-mp="1fjq8e0bc1aa">
      <JsonLd data={[{
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Bulk gravel and aggregate delivery',
      name: `Gravel delivery in ${city.name}, ${city.state}`,
      description: city.blurb,
      provider: localBusinessProvider(),
      areaServed: {
        '@type': 'City',
        name: `${city.name}, ${city.state}`
      },
      url: `https://206gravel.com${path}`,
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        lowPrice: lowestPrice,
        offerCount: allProducts.length,
        availability: 'https://schema.org/InStock'
      }
    }, {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://206gravel.com/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Service Areas',
        item: 'https://206gravel.com/service-areas'
      }, ...(county ? [{
        '@type': 'ListItem',
        position: 3,
        name: county.name,
        item: `https://206gravel.com/gravel-delivery/${county.slug}`
      }] : []), {
        '@type': 'ListItem',
        position: county ? 4 : 3,
        name: city.name,
        item: `https://206gravel.com${path}`
      }]
    }, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(f => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.a
        }
      }))
    }]} data-id="element-2465" data-mp="1hwgrfk4zjyy" />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-id="element-2466" data-mp="d5scfvc3t15w">
        <ol className="flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400" data-id="element-2467" data-mp="d863zu7krv8p">
          <li data-id="element-2468" data-mp="1v5ro8fg7m3iv">
            <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-2469" data-mp="202i3j0k6wv38">
              Home
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-2470" data-mp="1m3s61jtgktec" />
          <li data-id="element-2471" data-mp="1m4tkly9ony0t">
            <Link to="/service-areas" className="hover:text-brand-orange transition-colors" data-id="element-2472" data-mp="1r1tpw6ypkwqv">
              Service Areas
            </Link>
          </li>
          {county && <>
              <ChevronRight className="w-3 h-3" data-id="element-2473" data-mp="2etikjtz05xaz" />
              <li data-id="element-2474" data-mp="2it9bbxoj4dfi">
                <Link to={`/gravel-delivery/${county.slug}`} className="hover:text-brand-orange transition-colors" data-id="element-2475" data-mp="2irpuo5yyo0d7">
                  {county.name}
                </Link>
              </li>
            </>}
          <ChevronRight className="w-3 h-3" data-id="element-2476" data-mp="29z4m1ip1na23" />
          <li className="text-stone-900 dark:text-white font-semibold truncate" aria-current="page" data-id="element-2477" data-mp="2xq9z5q3lno8p">
            {city.name}
          </li>
        </ol>
      </nav>

      {/* Hero — copy column on the left, a quiet monochrome map plate filling
          the empty right-hand column on wide screens. The map is furniture:
          it confirms the place and gives the header a second focal point
          without pulling attention off the price CTA. Hidden below lg, where
          the column would collapse under the copy and just push the CTA and
          the stat row further down the page. */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12" data-id="element-2478" data-mp="2te4mmqprvf2m">
        <div className="grid gap-8 md:grid-cols-[minmax(0,1fr)_18rem] md:items-start lg:grid-cols-[minmax(0,1fr)_20rem]" data-id="element-2479" data-mp="2tg7gjlu1pscm">
        <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="max-w-3xl" data-id="element-2480" data-mp="2jo1u0rmkc34t">
          <div className="inline-flex items-center gap-2 mb-3" data-id="element-2481" data-mp="29q43fxh8du91">
            <MapPin className="w-3.5 h-3.5 text-brand-orange" data-id="element-2482" data-mp="29pc88nrjpyja" />
            <span className="text-[11px] font-bold text-brand-orange uppercase tracking-[0.14em]" data-id="element-2483" data-mp="25oaaych6vwjy">
              {city.name}, {city.state}
              {county ? ` · ${county.name}` : ''} · Zone {tier}
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900 dark:text-white leading-[1.05]" data-id="element-2484" data-mp="p69xdvfkquwl">
            Gravel Delivery in {city.name}, {city.state}
          </h1>
          <p className="mt-4 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2485" data-mp="z3wm19moe0tr">
            {city.blurb} Crushed rock, drain rock, topsoil, and bark by the
            cubic yard — hauled on our own trucks, with the delivered total
            shown before you order.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-2486" data-mp="bellnt98m8g5">
            <Link to="/#estimator" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm transition-colors shadow-lg shadow-brand-orange/20" data-id="element-2487" data-mp="g86jnlatz2fc">
              <CalculatorIcon className="w-4 h-4" data-id="element-2488" data-mp="3juf0m3nx7bv3" />
              Get a Delivered Price
              <ArrowRight className="w-4 h-4" data-id="element-2489" data-mp="1pgt4nyakuooa" />
            </Link>
            <a href="tel:206-650-0129" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-stone-300 dark:border-white/15 text-stone-900 dark:text-white font-bold text-sm hover:bg-stone-100 dark:hover:bg-white/5 transition-colors" data-id="element-2490" data-mp="1lfhb5hol1jat">
              <Phone className="w-4 h-4" data-id="element-2491" data-mp="1udjew4zpaq1s" />
              (206) 650-0129
            </a>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3 text-xs" data-id="element-2492" data-mp="2963eivnn6feh">
            <div className="px-3 py-2.5 rounded-lg bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2493" data-mp="244nu95tagcx5">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 font-bold mb-0.5" data-id="element-2494" data-mp="2i2kt68v7svez">
                Materials from
              </div>
              <div className="font-bold text-stone-900 dark:text-white" data-id="element-2495" data-mp="nrfrvb7lov2t">
                ${lowestPrice}/yd
              </div>
            </div>
            <div className="px-3 py-2.5 rounded-lg bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2496" data-mp="2ufg7f83n7tpa">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 font-bold mb-0.5" data-id="element-2497" data-mp="2zd7wlucwl1ps">
                Delivery here
              </div>
              <div className="font-bold text-stone-900 dark:text-white" data-id="element-2498" data-mp="2po7z6fe1uedp">
                +${fee}/load
              </div>
            </div>
            <div className="px-3 py-2.5 rounded-lg bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2499" data-mp="2bnx6d6t6ippw">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 font-bold mb-0.5" data-id="element-2500" data-mp="2glox3xi385si">
                Lead time
              </div>
              <div className="font-bold text-stone-900 dark:text-white" data-id="element-2501" data-mp="26qdf96bduigj">
                Same-day*
              </div>
            </div>
          </div>
        </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.08
        }} className="hidden md:block" data-id="element-2502" data-mp="4yqu3le69hrz">
            <Link to="/delivery-zones" className="group block" aria-label={`See ${city.name} on the delivery zone map`} data-id="element-2503" data-mp="18url427m2h4j">
              <CityMiniMap coords={city.coords} name={city.name} state={city.state} className="aspect-square w-full" data-id="element-2504" data-mp="14wc2eaikbuwx" />
              <span className="mt-2 flex items-center justify-between gap-2 text-xs font-bold text-stone-700 transition-colors group-hover:text-[#05894e] dark:text-stone-300 dark:group-hover:text-[#4ed491]" data-id="element-2505" data-mp="1wlf71red37a3">
                Zone {tier} · ${fee} per load
                <ArrowRight className="h-3.5 w-3.5 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" data-id="element-2506" data-mp="1nrioemkeoczf" />
              </span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Delivered-price transparency — the direct counter to "free delivery"
          marketing, stated with this city's actual fee rather than a range. */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2507" data-mp="2iv43l4yugnzy">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2508" data-mp="2ntp2n12d11mt">
          What delivery to {city.name} actually costs
        </h2>
        <p className="mt-3 max-w-3xl text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2509" data-mp="2wuwpmmzfeg1l">
          Nobody hauls rock for free. When a site advertises “free delivery,”
          the truck is paid for inside the per-yard price — which makes the
          material look cheaper and the real cost harder to check. Here it's
          one line: ${fee} per truckload to {city.name}, on top of the
          material.
        </p>
        {example && <div className="mt-6 p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 max-w-2xl" data-id="element-2510" data-mp="3gjxekb6aypsy">
            <div className="text-[11px] font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400" data-id="element-2511" data-mp="3pi9cky9i0pvf">
              Example — {EXAMPLE_YARDS} yd of {example.product.name} to{' '}
              {city.name}
            </div>
            <dl className="mt-4 space-y-2 text-sm" data-id="element-2512" data-mp="9o770q0ei2w6">
              <div className="flex items-baseline justify-between gap-4" data-id="element-2513" data-mp="om9az1bcgqdy">
                <dt className="text-stone-600 dark:text-stone-300" data-id="element-2514" data-mp="ok6ousdbbhei">
                  Material · {EXAMPLE_YARDS} yd × $
                  {example.product.pricePerYard}/yd
                </dt>
                <dd className="font-semibold tabular-nums text-stone-900 dark:text-white" data-id="element-2515" data-mp="3s39dlmmh88ub">
                  ${(example.product.pricePerYard * EXAMPLE_YARDS).toLocaleString()}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4" data-id="element-2516" data-mp="5p86ripm6ub2">
                <dt className="text-stone-600 dark:text-stone-300" data-id="element-2517" data-mp="v3uql1hrw34">
                  Delivery · Zone {tier}, one load
                </dt>
                <dd className="font-semibold tabular-nums text-stone-900 dark:text-white" data-id="element-2518" data-mp="34df6llou9ehz">
                  ${fee}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 pt-3 border-t border-stone-200 dark:border-white/10" data-id="element-2519" data-mp="1jts5rvd5jatm">
                <dt className="font-bold text-stone-900 dark:text-white" data-id="element-2520" data-mp="1onwd4fqbxkv8">
                  Delivered total
                </dt>
                <dd className="font-heading text-2xl font-extrabold tabular-nums text-stone-900 dark:text-white" data-id="element-2521" data-mp="1evqmpafq7bi5">
                  ${exampleTotal.toLocaleString()}
                </dd>
              </div>
            </dl>
            <p className="mt-4 text-xs text-stone-500 dark:text-stone-400" data-id="element-2522" data-mp="27jw7fx6s50hz">
              Sales tax applies at checkout. Enter your {city.name} address in
              the estimator for the exact figure on your yardage.
            </p>
          </div>}
        <div className="mt-6 grid sm:grid-cols-3 gap-4" data-id="element-2523" data-mp="1xom14x9cm6ye">
          {[{
          icon: ReceiptText,
          title: 'Delivered total up front',
          body: `Enter a ${city.name} address and yardage and the estimator returns the all-in price including the haul — no callback required.`
        }, {
          icon: Truck,
          title: 'Our trucks, our drivers',
          body: `We're the hauler, not a marketplace routing your order to whoever bids lowest. The driver who pulls into your ${city.name} driveway works for us.`
        }, {
          icon: CheckCircle2,
          title: 'Pay on delivery',
          body: 'No card at checkout. Cash, credit or debit card, Apple Pay, or Google Pay when the load arrives.'
        }].map(item => <div key={item.title} className="p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2524" data-mp="35q17wktaypu2">
              <item.icon className="w-5 h-5 text-brand-orange" data-id="element-2525" data-mp="2rvi0tw2eyy5f" />
              <h3 className="mt-3 font-heading text-base font-bold text-stone-900 dark:text-white" data-id="element-2526" data-mp="2wxqvqxyukaa6">
                {item.title}
              </h3>
              <p className="mt-1.5 text-sm text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2527" data-mp="2i1rhf5l59dwl">
                {item.body}
              </p>
            </div>)}
        </div>
      </section>

      {/* Local delivery access. The one part of this page a national broker
          generating city pages at scale cannot fake: which road we come in on,
          what the driveways are actually like, and how that changes the load.
          Authored per city in data/cityLocalContent.ts. */}
      {local && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2528" data-mp="28e5owtqfnh0t">
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8" data-id="element-2529" data-mp="2ia8mdmacarhf">
            <div data-id="element-2530" data-mp="2i7wa9pr9jbca">
              <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2531" data-mp="2w1ds964awg07">
                Delivering in {city.name} — access and conditions
              </h2>
              <p className="mt-3 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2532" data-mp="35zkvyn9kx1k9">
                {local.access}
              </p>
              <Link to="/delivery-prep" className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-stone-900 dark:text-white hover:text-brand-orange transition-colors" data-id="element-2533" data-mp="3119ogj6rb357">
                <ClipboardList className="w-4 h-4 text-brand-orange" data-id="element-2534" data-mp="3d287cqcmmdtd" />
                How to prep your drop spot
                <ArrowRight className="w-3.5 h-3.5" data-id="element-2535" data-mp="3i1kr3287b4rq" />
              </Link>
            </div>
            {/* The hard numbers a customer needs before they can decide
                anything. Previously these were buried inside one FAQ answer. */}
            <div className="p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 self-start" data-id="element-2536" data-mp="3rtqk8fa6vs8i">
              <div className="flex items-center gap-2" data-id="element-2537" data-mp="4n7qytmr0tlh">
                <Ruler className="w-4 h-4 text-brand-orange" data-id="element-2538" data-mp="4nr662l0l3vt" />
                <h3 className="text-[11px] font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400" data-id="element-2539" data-mp="9mas42zlyfo6">
                  What the truck needs
                </h3>
              </div>
              <dl className="mt-4 space-y-3 text-sm" data-id="element-2540" data-mp="2bcmbftmxoio9">
                {[{
              k: 'Minimum order',
              v: '3 cubic yards ($250 flat fee under 3 yd)'
            }, {
              k: 'Full load',
              v: '12 yd rock and soil · 15 yd bark and chips'
            }, {
              k: 'Sold by',
              v: 'The cubic yard, not by the ton'
            }, {
              k: 'Truck',
              v: 'Tandem dump — drop spot approx. 10 ft × 20 ft'
            }, {
              k: 'Overhead clearance',
              v: 'About 12 ft to raise the bed and dump'
            }, {
              k: 'Placement',
              v: 'One pile within reach of the bed — we do not spread'
            }, {
              k: 'Delivery to ' + city.name,
              v: `${fee} per truckload, Zone ${tier}`
            }].map(row => <div key={row.k} className="flex flex-col gap-0.5" data-id="element-2541" data-mp="27v8dhxq2odqp">
                    <dt className="text-[11px] font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400" data-id="element-2542" data-mp="11gh4f6qspmll">
                      {row.k}
                    </dt>
                    <dd className="text-stone-800 dark:text-stone-200 leading-snug" data-id="element-2543" data-mp="2lasaaugqbsik">
                      {row.v}
                    </dd>
                  </div>)}
              </dl>
            </div>
          </div>

          {/* Project guides matched to the work this city actually orders, so
              the internal links differ page to page instead of every city
              pointing at all eight guides. */}
          {localProjects.length > 0 && <div className="mt-10 pt-8 border-t border-stone-200 dark:border-white/10" data-id="element-2544" data-mp="341jhpqh5p8ub">
              <h3 className="font-heading text-xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2545" data-mp="38ys4y5h5w32f">
                Common {city.name} projects
              </h3>
              <div className="mt-5 grid sm:grid-cols-3 gap-4" data-id="element-2546" data-mp="2z14899zzwlhm">
                {localProjects.map(project => <Link key={project.slug} to={`/projects/${project.slug}`} className="group p-5 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 hover:border-brand-orange transition-colors" data-id="element-2547" data-mp="1in5ns1djo9s1">
                    <project.icon className="w-5 h-5 text-brand-orange" data-id="element-2548" data-mp="5nu4y8h89ybx" />
                    <h4 className="mt-3 font-heading text-base font-bold text-stone-900 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-2549" data-mp="qttfi1m03df">
                      {project.name}
                    </h4>
                    <p className="mt-1.5 text-sm text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2550" data-mp="apjefjosy3xw">
                      {project.tagline}
                    </p>
                    <p className="mt-3 text-xs font-bold text-stone-800 dark:text-white inline-flex items-center gap-1" data-id="element-2551" data-mp="3e56w9yeg4snd">
                      Materials and depths
                      <ArrowRight className="w-3 h-3" data-id="element-2552" data-mp="3ryg6u2e4rk7h" />
                    </p>
                  </Link>)}
              </div>
            </div>}
        </section>}

      {/* Real completed deliveries in this city — photo, material, quantity,
          and the customer's own words. Only renders where we actually have a
          photographed job. */}
      {deliveryExamples.length > 0 && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2553" data-mp="yd9z1476e45m">
          <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2554" data-mp="13atajwcho2tv">
            Recent deliveries in {city.name}
          </h2>
          <p className="mt-2 text-stone-600 dark:text-stone-300 max-w-2xl" data-id="element-2555" data-mp="1d1e9jkw2r9db">
            Photos from real {city.name} jobs, sent in by the customers who ordered them.
          </p>
          <div className={`mt-6 grid gap-5 ${deliveryExamples.length === 1 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'}`} data-id="element-2556" data-mp="kd8oex13zicy">
            {deliveryExamples.map(example => <figure key={example.photo} className="flex flex-col rounded-2xl overflow-hidden bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2557" data-mp="uc7wade1v9yw">
                <img src={example.photo} alt={`${example.materialUsed ?? 'Bulk material'} delivered to a ${example.role.toLowerCase()} property in ${example.location.replace(/, WA$/, '')}`} loading="lazy" className="w-full aspect-[4/3] object-cover" data-id="element-2558" data-mp="20s0hzd9zqvnu" />
                <figcaption className="flex flex-1 flex-col p-5" data-id="element-2559" data-mp="3la2kr405my9l">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-2560" data-mp="3lcounaaippuz">
                    {example.materialUsed && <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-brand-orange/10 text-brand-orange" data-id="element-2561" data-mp="3gh9hylo8y005">
                        {example.materialUsed}
                      </span>}
                    {example.volume && <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400" data-id="element-2562" data-mp="32irwkultczle">
                        {example.volume}
                      </span>}
                  </div>
                  <p className="mt-3 text-sm text-stone-700 dark:text-stone-300 leading-relaxed" data-id="element-2563" data-mp="2smyl7ejtzc57">
                    “{example.content}”
                  </p>
                  <p className="mt-auto pt-4 text-xs font-bold text-stone-900 dark:text-white" data-id="element-2564" data-mp="qujxtzyipfhe">
                    {example.name}
                    <span className="font-medium text-stone-500 dark:text-stone-400" data-id="element-2565" data-mp="3vvn6ranwtty7">
                      {' '}· {example.location.replace(/, WA$/, '')}
                    </span>
                  </p>
                </figcaption>
              </figure>)}
          </div>
        </section>}

      {/* Materials. One block, two link targets: the four curated materials
          get their own city × material pages, everything else routes into the
          catalog. Previously these were two near-identical grids stacked on
          top of each other. */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2566" data-mp="335odf32onanq">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2567" data-mp="38481pa4r0yj9">
          Materials delivered in {city.name}
        </h2>
        <p className="mt-3 max-w-3xl text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2568" data-mp="11cgyvvuw8x2w">
          Crushed rock, drain rock, pea gravel, bark, and topsoil — all stocked
          and hauled into {city.name} on our own trucks. These four move the
          most:
        </p>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2569" data-mp="2lxx8p84o1x2f">
          {featured.map(({
          product
        }) => <Link key={product.slug} to={`/gravel-delivery/${city.slug}/${product.slug}`} className="group rounded-2xl overflow-hidden bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 hover:border-brand-orange transition-colors" data-id="element-2570" data-mp="30n1qio7jtza0">
              <img src={product.image} alt={product.name} className="w-full h-32 object-cover" loading="lazy" data-id="element-2571" data-mp="35ja3buy9xge0" />
              <div className="p-4" data-id="element-2572" data-mp="3jjkrurc42iw0">
                <h3 className="font-heading text-base font-bold text-stone-900 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-2573" data-mp="3jhqfi1ewnz3j">
                  {product.name}
                </h3>
                <p className="mt-1 text-xs text-stone-500 dark:text-stone-400" data-id="element-2574" data-mp="1p7wjj80dxekl">
                  ${product.pricePerYard}/yd + ${fee} delivery
                </p>
                <p className="mt-2 text-xs font-bold text-stone-800 dark:text-white inline-flex items-center gap-1" data-id="element-2575" data-mp="t4nwi484wcgu">
                  {product.name} in {city.name}
                  <ArrowRight className="w-3 h-3" data-id="element-2576" data-mp="kc2fask5idu4" />
                </p>
              </div>
            </Link>)}
        </div>

        {alsoPopular.length > 0 && <div className="mt-8 pt-6 border-t border-stone-200 dark:border-white/10" data-id="element-2577" data-mp="fbohs4g6emac">
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400" data-id="element-2578" data-mp="ag953ftwmwfi">
              Also delivered in {city.name}
            </h3>
            <ul className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2.5" data-id="element-2579" data-mp="1f18d271e3nj">
              {alsoPopular.map(({
            product,
            use
          }) => <li key={product.slug} data-id="element-2580" data-mp="1vs0tfau6rq27">
                  <Link to={`/shop/${product.slug}`} className="group flex items-baseline gap-2 text-sm" data-id="element-2581" data-mp="1vqzjxastjbml">
                    <span className="font-bold text-stone-800 dark:text-white group-hover:text-brand-orange transition-colors shrink-0" data-id="element-2582" data-mp="1qu948okxei1p">
                      {product.name}
                    </span>
                    <span className="text-stone-500 dark:text-stone-400 truncate" data-id="element-2583" data-mp="23a8qkw9xww7y">
                      {use}
                    </span>
                  </Link>
                </li>)}
            </ul>
          </div>}
      </section>

      {/* Local vocabulary. A national broker writes "3/4 crushed stone"; the
          yards, contractors, and customers here don't. Naming the materials
          the way the region actually names them is both a ranking signal and
          a trust signal. */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2584" data-mp="1papi22d7viv4">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2585" data-mp="1tex8q538j8vz">
          We use the names {city.name} yards and contractors use
        </h2>
        <p className="mt-3 max-w-3xl text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2586" data-mp="34r7uhrjka4qx">
          Around Puget Sound, gravel is sold by our own vocabulary. If you've
          been quoted “3/4 crushed stone” or “#57 stone,” you're talking to
          somebody who isn't from here. Here's what to ask for:
        </p>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2587" data-mp="1eno2dwoivmo8">
          {[{
          term: '5/8" Minus',
          body: 'Crushed rock with the fines left in, so it compacts hard. The default driveway and pathway surface here.'
        }, {
          term: '7/8" Drain Rock',
          body: 'Washed and screened with no fines, so water runs straight through. French drains, footing drains, dry wells.'
        }, {
          term: '3-Way Topsoil',
          body: 'Sand, compost, and screened soil blended three ways. The standard lawn and garden bed mix in the region.'
        }, {
          term: 'Medium Bark',
          body: 'Fir and hemlock bark for beds and slopes — what “mulch” means locally, and what holds on our winter rain.'
        }].map(item => <div key={item.term} className="p-5 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2588" data-mp="29whxuhohayqs">
              <h3 className="font-heading text-base font-bold text-stone-900 dark:text-white" data-id="element-2589" data-mp="2eqdspb9g45hq">
                {item.term}
              </h3>
              <p className="mt-1.5 text-sm text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2590" data-mp="25vfym0ukamot">
                {item.body}
              </p>
            </div>)}
        </div>
      </section>

      {/* Neighborhoods + full catalog */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2591" data-mp="2xlu7finpx8tm">
        <div className="grid lg:grid-cols-2 gap-8" data-id="element-2592" data-mp="2jn44vni4qgxv">
          <div data-id="element-2593" data-mp="2olnwa3fhskxm">
            <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2594" data-mp="2ol5qaf312sci">
              Gravel delivery throughout {city.name}
            </h2>
            <p className="mt-3 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2595" data-mp="1d7k2oyctgyt3">
              We deliver gravel, crushed rock, drain rock, topsoil, and
              landscaping materials throughout {city.name} — including{' '}
              {city.neighborhoods.join(', ')} and the surrounding communities.
              Residential driveways, landscape beds, and contractor jobsites
              alike.
            </p>
            <ul className="mt-5 flex flex-wrap gap-2" data-id="element-2596" data-mp="1eqtuisp5wkui">
              {city.neighborhoods.map(n => <li key={n} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 text-stone-700 dark:text-stone-200" data-id="element-2597" data-mp="2berqjzwzuu3e">
                  {n}
                </li>)}
            </ul>
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-stone-600 dark:text-stone-300" data-id="element-2598" data-mp="1xg0esk5ltkif">
              <Clock className="w-4 h-4 text-brand-orange" data-id="element-2599" data-mp="qzodia7hjmim" />
              Same-day on most orders placed before noon
            </div>
          </div>
          <div data-id="element-2600" data-mp="lza3wnq8k4ih">
            <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2601" data-mp="m2xy7p3i8uy9">
              Full catalog, delivered to {city.name}
            </h2>
            <div className="mt-5 space-y-5" data-id="element-2602" data-mp="gwizvc7sz53t">
              {categories.map(cat => <div key={cat.name} data-id="element-2603" data-mp="luu9pmy3lfm1">
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400" data-id="element-2604" data-mp="ltsy9hd66r3s">
                    {cat.name}
                  </h3>
                  <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5" data-id="element-2605" data-mp="ume5q1bcpbdb">
                    {cat.products.map(p => <li key={p.slug} data-id="element-2606" data-mp="zlharlrbzhla">
                        <Link to={`/shop/${p.slug}`} className="text-sm text-stone-700 dark:text-stone-300 hover:text-brand-orange transition-colors" data-id="element-2607" data-mp="zolkeynkf5jc">
                          {p.name}{' '}
                          <span className="text-stone-400 dark:text-stone-500" data-id="element-2608" data-mp="19jwzji2vyerm">
                            ${p.pricePerYard}
                          </span>
                        </Link>
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2609" data-mp="2yciteqgvar5r">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2610" data-mp="3d9taqgg6yr9e">
          {city.name} gravel delivery questions
        </h2>
        <div className="mt-6 grid md:grid-cols-2 gap-4" data-id="element-2611" data-mp="3ma7xzlcpugfj">
          {faqs.map(faq => <div key={faq.q} className="p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2612" data-mp="3m84zecxhzez7">
              <h3 className="font-heading text-base font-bold text-stone-900 dark:text-white" data-id="element-2613" data-mp="3vzri0uc60gbz">
                {faq.q}
              </h3>
              <p className="mt-2 text-sm text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2614" data-mp="3r29c7ctg2h87">
                {faq.a}
              </p>
            </div>)}
        </div>
      </section>

      {/* Nearby cities + final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2615" data-mp="4rn5n5llk76b">
        {nearby.length > 0 && <div className="mb-8" data-id="element-2616" data-mp="shmywp2ae6iw">
            <span className="text-xs font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400" data-id="element-2617" data-mp="sglcjoftlxlv">
              Also delivering nearby
            </span>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2" data-id="element-2618" data-mp="jfx7vkcbh9ln">
              {nearby.map(c => <Link key={c.slug} to={`/gravel-delivery/${c.slug}`} className="inline-flex items-center gap-1.5 text-sm font-bold text-stone-800 dark:text-white hover:text-brand-orange transition-colors" data-id="element-2619" data-mp="4rsosf9cqw2c">
                  {c.name}
                  <ArrowRight className="w-3.5 h-3.5" data-id="element-2620" data-mp="3rx9uwy2i5hra" />
                </Link>)}
            </div>
          </div>}
        <div className="p-8 rounded-2xl bg-stone-900 dark:bg-black/40 border border-stone-800 dark:border-white/10" data-id="element-2621" data-mp="3vyn3hfwzbz1w">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-white tracking-tight" data-id="element-2622" data-mp="3hyudxnx6vovi">
            Get your {city.name} delivered price in about a minute
          </h2>
          <p className="mt-3 max-w-2xl text-stone-300 leading-relaxed" data-id="element-2623" data-mp="6995ah0539pf">
            Enter your address, pick a material and yardage, and see the all-in
            total — material plus haul — before you commit to anything.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-2624" data-mp="3tgbghjd8cwqh">
            <Link to="/#estimator" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm transition-colors" data-id="element-2625" data-mp="3oew0i5pwtgey">
              <CalculatorIcon className="w-4 h-4" data-id="element-2626" data-mp="3eo1aqrdgfyor" />
              Get a Delivered Price
              <ArrowRight className="w-4 h-4" data-id="element-2627" data-mp="39ravg69h574e" />
            </Link>
            <a href="tel:206-650-0129" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/20 text-white font-bold text-sm hover:bg-white/10 transition-colors" data-id="element-2628" data-mp="35p64jfaeuolg">
              <Phone className="w-4 h-4" data-id="element-2629" data-mp="2vr030ysinkh4" />
              (206) 650-0129
            </a>
          </div>
          <p className="mt-5 text-[11px] text-stone-400" data-id="element-2630" data-mp="1b8xx4z7231r5">
            *Same-day delivery subject to truck availability and order cutoff
            times.
          </p>
        </div>
      </section>
    </main>;
}